"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import { useCurrency } from "@/contexts/CurrencyContext";
import { cn } from "@/lib/utils";
import type { ProductOption } from "@/types";
import type { SupportedCurrency } from "@/lib/utils/currency";
import {
  isPerDayOptionOrder,
  perDayOptionTotal,
} from "@/lib/mobility/per-day-options";

const NO_PER_DAY_ORDERS: ReadonlySet<number> = new Set<number>();

/** 선택지가 이 개수를 넘으면 펼침 영역을 내부 스크롤로 고정 (타임슬롯과 동일 임계) */
const OPTION_SCROLL_THRESHOLD = 5;

interface BookingOptionsSectionProps {
  options: ProductOption[];
  sourceCurrency: SupportedCurrency;
  selectedOptions: Record<
    string,
    { enabled: boolean; value?: string | number }
  >;
  /** 필수인데 아직 입력되지 않은 옵션의 id 목록 — 빨간 테두리 하이라이트용 */
  missingRequiredOptionIds?: Set<string>;
  onOptionSelect: (optionId: string, value: string | number) => void;
  /**
   * 하루당 옵션(09-30) — `specifications.perDayOptionOrders` 의 display_order 집합.
   * 이 옵션은 가격 뒤에 "/ 일", 선택하면 상자 아래에 "대여 N일 × 단가 = 총액" 줄.
   */
  perDayOrders?: ReadonlySet<number>;
  /** 담긴 대여의 일수 합(없으면 null = 날짜 전 — "날짜를 고르면 계산" 안내) */
  perDayDays?: number | null;
  /**
   * 하루당 옵션의 현재 선택(옵션 id → 선택지 라벨). 일반 옵션은 고르면 선택 내역으로
   * 넘어가고 상자가 비워지지만, 하루당 옵션은 단일 선택이라 상자가 선택을 계속 보여준다.
   */
  perDaySelections?: Record<string, string>;
}

export default function BookingOptionsSection({
  options,
  sourceCurrency,
  selectedOptions,
  missingRequiredOptionIds,
  onOptionSelect,
  perDayOrders = NO_PER_DAY_ORDERS,
  perDayDays = null,
  perDaySelections,
}: BookingOptionsSectionProps) {
  const t = useTranslations("product.booking");
  const tPerDay = useTranslations("product.perDayOption");
  const { formatFromSource } = useCurrency();

  // 한 번에 하나의 옵션만 펼침 (단일 오픈 아코디언).
  // Radix Select(Portal)는 viewport safe-area를 모르고 목록 양 끝이 status bar/홈
  // 인디케이터 뒤로 들어가 탭이 안 되므로, 문서 흐름(in-flow) 안에서 펼치는 인라인
  // 아코디언으로 대체한다. Drawer 스크롤(pb-safe)로 양 끝까지 도달 가능.
  const [openOptionId, setOpenOptionId] = useState<string | null>(null);

  // 펼친 옵션이 내부 스크롤될 때 "더 있음" 힌트(하단 페이드+화살표) 제어.
  // 단일 오픈이라 한 번에 한 목록만 떠서 스크롤 상태도 하나면 충분하다.
  const optionScrollRef = useRef<HTMLDivElement>(null);

  /**
   * 펼친 상자를 화면 안으로. 가장 가까운 스크롤 컨테이너(드로어 본문 또는 창)를 기준으로
   * 상자 하단이 잘려 있으면 그 차이만큼만 내린다 — 위쪽은 건드리지 않아 헤더가 튀지 않는다.
   */
  const revealExpanded = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    requestAnimationFrame(() => {
      const box = node.closest("[data-option-box]") ?? node;
      box.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  }, []);
  const [optionAtBottom, setOptionAtBottom] = useState(false);
  const handleOptionScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setOptionAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
  };

  // 필수 옵션을 맨 위로 — 파트너가 정한 display_order 는 필수/선택 그룹 안에서만
  // 유지한다. 예약 버튼을 막는 항목이 목록 끝(스크롤 아래)에 숨어 있으면 고객이
  // 왜 버튼이 비활성인지 찾지 못한다(Bestbike "차량 보상" 사례, 09-18).
  const orderedOptions = useMemo(
    () =>
      [...(options ?? [])].sort(
        (a, b) =>
          Number(!!b.is_required) - Number(!!a.is_required) ||
          (a.display_order ?? 0) - (b.display_order ?? 0),
      ),
    [options],
  );

  if (orderedOptions.length === 0) return null;

  return (
    <div>
      <p className="font-semibold mb-4 text-sm flex items-center gap-2">
        <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full ring-3 bg-white ring-orange-500" />
        {t("options")}
      </p>
      {/* 12px: 필수/추천 배지가 박스 위로 -top-2(8px) 떠 있어 그보다 좁히면
          위 박스에 닿는다 — 8px 축소 금지. */}
      <div className="space-y-3">
        {orderedOptions.map((option) => {
          if (option.option_type === "select") {
            const choices = option.choices || [];
            if (choices.length === 0) return null;

            const isMissing = missingRequiredOptionIds?.has(option.id);
            const isOpen = openOptionId === option.id;
            const isPerDay = isPerDayOptionOrder(
              perDayOrders,
              option.display_order,
            );
            const selectedValue = isPerDay
              ? perDaySelections?.[option.id]
              : selectedOptions[option.id]?.value;
            const selectedChoice = choices.find(
              (choice) => choice.label === selectedValue
            );
            const isScrollable = choices.length > OPTION_SCROLL_THRESHOLD;

            return (
              <div key={option.id} className="relative">
                {option.is_required && (
                  <span
                    className={cn(
                      "absolute -top-2 left-2 z-10 px-1.5 py-0.5 rounded-full text-[10px] leading-none font-medium text-white pointer-events-none ring-2 ring-white",
                      isMissing ? "bg-red-500" : "bg-orange-500"
                    )}
                  >
                    {t("requiredBadge") || "필수"}
                  </span>
                )}

                {/* 헤더 + 펼침 영역을 하나의 박스로. 필수 미선택이면 빨간 테두리. */}
                <div
                  data-option-box
                  className={cn(
                    "rounded-md border bg-white overflow-hidden transition-colors",
                    isMissing
                      ? "border-red-500 ring-1 ring-red-500"
                      : "border-input"
                  )}
                >
                  {/* 접힘 행(헤더) — 드롭다운 트리거처럼 보이지만 in-flow 토글 */}
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => {
                      const next = isOpen ? null : option.id;
                      setOpenOptionId(next);
                      // 새로 펼치면 목록 맨 위 → 아직 바닥 아님(힌트 표시).
                      if (next) setOptionAtBottom(false);
                    }}
                    className="flex items-center justify-between w-full gap-2 px-3 py-2 min-h-9 text-sm text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <span
                      className={cn(
                        // 긴 옵션명·선택 요약은 말줄임 대신 2줄까지 (08-20)
                        "line-clamp-2",
                        selectedChoice
                          ? "text-gray-900"
                          : "text-muted-foreground"
                      )}
                    >
                      {selectedChoice
                        ? isPerDay
                          ? perDayDays != null
                            ? tPerDay("headerWithDays", {
                                label: selectedChoice.label,
                                price: formatFromSource(
                                  selectedChoice.price,
                                  sourceCurrency
                                ),
                                days: perDayDays,
                              })
                            : tPerDay("headerNoDays", {
                                label: selectedChoice.label,
                                price: formatFromSource(
                                  selectedChoice.price,
                                  sourceCurrency
                                ),
                              })
                          : `${selectedChoice.label} (+ ${formatFromSource(
                              selectedChoice.price,
                              sourceCurrency
                            )})`
                        : option.name}
                    </span>
                    <CaretDownIcon
                      className={cn(
                        "h-4 w-4 shrink-0 text-gray-500 transition-transform",
                        isOpen && "rotate-180"
                      )}
                    />
                  </button>

                  {/* 펼침 영역 — 타임슬롯 버튼 리스트 패턴 재사용.
                      마운트 직후 펼친 상자가 화면 아래로 잘리면 그만큼 스크롤해 끝까지
                      보이게 한다(09-22). 콜백 ref 라 렌더 뒤 한 번만 실행되고 setState 없음. */}
                  {isOpen && (
                    <div
                      ref={revealExpanded}
                      className="relative border-t border-gray-200 bg-gray-50"
                    >
                      <div
                        ref={isScrollable ? optionScrollRef : undefined}
                        onScroll={isScrollable ? handleOptionScroll : undefined}
                        className={cn(
                          "divide-y divide-gray-200",
                          // 선택지가 많으면 고정 높이 내부 스크롤 → 카드가 무한정 길어지지 않게.
                          isScrollable &&
                            "overflow-y-auto overscroll-contain max-h-60"
                        )}
                      >
                        {choices.map((choice, idx) => {
                          const selected = choice.label === selectedValue;
                          return (
                            <button
                              key={idx}
                              type="button"
                              className={cn(
                                "flex items-center justify-between w-full py-2.5 px-3 text-left text-sm transition-colors gap-3",
                                selected
                                  ? "bg-orange-50 cursor-pointer"
                                  : "hover:bg-gray-100 cursor-pointer"
                              )}
                              onClick={() => {
                                onOptionSelect(option.id, choice.label);
                                setOpenOptionId(null);
                              }}
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                {/* 체크박스 (단일 선택이지만 타임슬롯처럼 사각 체크) */}
                                <span
                                  className={cn(
                                    "flex items-center justify-center w-5 h-5 rounded border shrink-0 transition-colors",
                                    selected
                                      ? "bg-orange-500 border-orange-500"
                                      : "bg-white border-gray-300"
                                  )}
                                >
                                  {selected && (
                                    <CheckIcon
                                      className="w-3.5 h-3.5 text-white"
                                      weight="bold"
                                    />
                                  )}
                                </span>
                                <span
                                  className={cn(
                                    "line-clamp-2",
                                    selected
                                      ? "text-orange-700"
                                      : "text-gray-900"
                                  )}
                                >
                                  {choice.label}
                                </span>
                              </div>
                              <span
                                className={cn(
                                  "whitespace-nowrap shrink-0",
                                  selected
                                    ? "text-orange-700 font-medium"
                                    : "text-gray-500"
                                )}
                              >
                                {isPerDay
                                  ? tPerDay("unitPrice", {
                                      price: formatFromSource(
                                        choice.price,
                                        sourceCurrency
                                      ),
                                    })
                                  : formatFromSource(choice.price, sourceCurrency)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {/* 내부스크롤 안내: 하단 페이드 + ↓ (바닥 도달 전까지만) */}
                      {isScrollable && !optionAtBottom && (
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-9 flex items-end justify-center bg-linear-to-t from-gray-100 to-transparent pb-0.5">
                          <CaretDownIcon className="h-4 w-4 text-gray-500 animate-bounce" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* 하루당 옵션 계산 줄 (09-30) — 선택했을 때만. 날짜(담긴 대여)가 있으면
                      "대여 N일 × 단가 = 총액", 없으면 날짜 선택 안내. */}
                  {isPerDay && selectedChoice && (
                    <p className="border-t border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 break-keep">
                      {perDayDays != null
                        ? tPerDay.rich("calc", {
                            days: perDayDays,
                            price: formatFromSource(
                              selectedChoice.price,
                              sourceCurrency
                            ),
                            total: formatFromSource(
                              perDayOptionTotal(selectedChoice.price, perDayDays),
                              sourceCurrency
                            ),
                            b: (chunks) => (
                              <b className="font-semibold text-gray-900">
                                {chunks}
                              </b>
                            ),
                          })
                        : tPerDay("pickDates")}
                    </p>
                  )}
                </div>

                {isMissing && (
                  <p className="mt-1 text-xs text-red-600">
                    {t("requiredOption") || "필수 옵션입니다"}
                  </p>
                )}
              </div>
            );
          }

          // Input/File type options - skip (entered at checkout)
          if (option.option_type === "input" || option.option_type === "file") {
            return null;
          }

          // Unknown option type
          console.warn(
            `Unknown option type: ${option.option_type} for option: ${option.name}`
          );
          return (
            <div
              key={option.id}
              className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm"
            >
              <p className="font-medium text-yellow-800">
                {option.name}
                {option.is_required && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </p>
              <p className="text-yellow-600 text-xs mt-1">
                지원하지 않는 옵션 타입입니다 (type: {option.option_type})
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
